/**
 * The TTS catalogue, fetched live from the backend's `GET /tts/catalog`, so the provider, model and
 * voice selects show exactly what the deployment this console is pointed at can actually speak with
 * — not a list baked into the build. One request, every provider with its availability, models and
 * voices; the UI reads it the way it used to read the hardcoded catalog.
 */

/** A real TTS provider id, as the gateway spells it in `metadata.tts.provider`. */
export type TtsProvider = 'polly' | 'elevenlabs' | 'gemini';

/** A model a provider offers. `name` is for display; `id` is what the handshake carries. */
export interface CatalogModel {
  id: string;
  name: string;
  /**
   * Whether this model supports streaming synthesis. The backend already omits non-streaming models
   * from the catalogue, so this is usually absent (treated as streamable); it is honoured when
   * present so a non-streaming model is hidden defensively on the client too.
   */
  streaming?: boolean;
}

/** A voice a provider offers. `name` is for display; `id` is what the handshake carries. */
export interface CatalogVoice {
  id: string;
  name: string;
  gender?: string;
}

/** One provider as the catalogue describes it: whether it is usable, and its models and voices. */
export interface CatalogProvider {
  id: TtsProvider;
  name: string;
  available: boolean;
  models: CatalogModel[];
  voices: CatalogVoice[];
}

/** The whole catalogue: every provider the backend knows, in the order it returns them. */
export type TtsCatalog = readonly CatalogProvider[];

const KNOWN_PROVIDERS: readonly TtsProvider[] = ['polly', 'elevenlabs', 'gemini'];

/**
 * The HTTP URL of the catalogue endpoint for a given gateway endpoint. The gateway is a WebSocket
 * URL ending in `/ws` (or a same-origin `/ws` path); the catalogue is its REST sibling, so the
 * scheme drops from ws(s) to http(s) and the trailing `/ws` becomes `/tts/catalog`.
 *
 * @param resolvedEndpoint an absolute `ws://`/`wss://` URL, as resolveEndpoint returns
 * @returns the absolute `http(s)://…/tts/catalog` URL
 */
export function catalogUrl(resolvedEndpoint: string): string {
  const url = new URL(resolvedEndpoint);
  url.protocol =
    url.protocol === 'wss:' ? 'https:' : url.protocol === 'ws:' ? 'http:' : url.protocol;
  // The gateway path ends in `/ws`; the catalogue is a sibling. Replace only the final segment so
  // any context path before it (e.g. `/andromeda/ws`) is preserved.
  url.pathname = url.pathname.replace(/\/ws\/?$/, '/tts/catalog');
  url.search = '';
  url.hash = '';
  return url.toString();
}

/**
 * Fetches the catalogue from the given gateway endpoint, normalising the response into providers
 * this console knows. A provider the backend does not list, or one it lists under an id this build
 * does not know, is dropped rather than shown as a dead option.
 *
 * @param resolvedEndpoint an absolute `ws://`/`wss://` gateway URL
 * @param signal an optional abort signal, so a stale fetch can be cancelled on endpoint change
 * @returns the normalised catalogue
 * @throws when the request fails or the body is not the expected shape
 */
export async function fetchTtsCatalog(
  resolvedEndpoint: string,
  signal?: AbortSignal,
): Promise<TtsCatalog> {
  const response = await fetch(catalogUrl(resolvedEndpoint), {
    headers: { accept: 'application/json' },
    signal,
  });
  if (!response.ok) {
    throw new Error(`TTS catalog request failed: ${response.status}`);
  }
  const body: unknown = await response.json();
  if (!Array.isArray(body)) {
    throw new Error('TTS catalog response was not a list');
  }
  return body
    .map(normalizeProvider)
    .filter((provider): provider is CatalogProvider => provider !== null);
}

/** Narrows one raw provider entry, or null for anything that is not a provider this build knows. */
function normalizeProvider(raw: unknown): CatalogProvider | null {
  if (raw === null || typeof raw !== 'object') return null;
  const entry = raw as Record<string, unknown>;
  const id = entry.id;
  if (typeof id !== 'string' || !KNOWN_PROVIDERS.includes(id as TtsProvider)) return null;

  return {
    id: id as TtsProvider,
    name: typeof entry.name === 'string' ? entry.name : id,
    available: entry.available === true,
    models: normalizeEntries(entry.models),
    voices: normalizeVoices(entry.voices),
  };
}

function normalizeEntries(raw: unknown): CatalogModel[] {
  if (!Array.isArray(raw)) return [];
  return (
    raw
      .map((item): CatalogModel | null => {
        if (item === null || typeof item !== 'object') return null;
        const entry = item as Record<string, unknown>;
        if (typeof entry.id !== 'string') return null;
        return {
          id: entry.id,
          name: typeof entry.name === 'string' ? entry.name : entry.id,
          streaming: typeof entry.streaming === 'boolean' ? entry.streaming : undefined,
        };
      })
      .filter((model): model is CatalogModel => model !== null)
      // The backend already omits non-streaming models; drop any that still carry streaming:false.
      .filter((model) => model.streaming !== false)
  );
}

function normalizeVoices(raw: unknown): CatalogVoice[] {
  if (!Array.isArray(raw)) return [];
  return raw
    .map((item): CatalogVoice | null => {
      if (item === null || typeof item !== 'object') return null;
      const entry = item as Record<string, unknown>;
      if (typeof entry.id !== 'string') return null;
      return {
        id: entry.id,
        name: typeof entry.name === 'string' ? entry.name : entry.id,
        gender: typeof entry.gender === 'string' ? entry.gender : undefined,
      };
    })
    .filter((voice): voice is CatalogVoice => voice !== null);
}
